class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.inputTimer = null;
    this.abortController = null;
    this.isOpen = false;

    this.onInput = this.onInput.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onKeyup = this.onKeyup.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
  }

  connectedCallback() {
    if (this.dataset.predictiveSearchInitialized === 'true') return;

    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form.search, form[role="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');

    if (!this.input || !this.form || !this.predictiveSearchResults) {
      console.warn(
        'Predictive search could not initialize. Required search markup is missing.',
        this
      );
      return;
    }

    this.setupEventListeners();
    this.dataset.predictiveSearchInitialized = 'true';
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.cancelPendingRequest();

    if (this.inputTimer) {
      window.clearTimeout(this.inputTimer);
      this.inputTimer = null;
    }

    delete this.dataset.predictiveSearchInitialized;
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('focus', this.onFocus);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keyup', this.onKeyup);
    this.addEventListener('keydown', this.onKeydown);
  }

  removeEventListeners() {
    if (!this.form || !this.input) return;

    this.form.removeEventListener('submit', this.onFormSubmit);
    this.input.removeEventListener('input', this.onInput);
    this.input.removeEventListener('focus', this.onFocus);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('keyup', this.onKeyup);
    this.removeEventListener('keydown', this.onKeydown);
  }

  onInput() {
    if (this.inputTimer) {
      window.clearTimeout(this.inputTimer);
    }

    this.inputTimer = window.setTimeout(() => {
      this.onChange();
    }, 250);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    const selectedResult = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }

    if (selectedResult) {
      event.preventDefault();
      selectedResult.click();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    window.setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    }, 100);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;

      case 'Enter':
        this.selectOption();
        break;

      case 'Escape':
        this.close();
        this.input.focus();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  getSelectableElements() {
    return Array.from(
      this.predictiveSearchResults.querySelectorAll(
        '[role="option"], li'
      )
    ).filter((element) =>
      element.querySelector('a, button')
    );
  }

  switchOption(direction) {
    if (!this.hasAttribute('open')) return;

    const options = this.getSelectableElements();

    if (!options.length) return;

    const selectedElement = this.querySelector('[aria-selected="true"]');
    const selectedIndex = selectedElement
      ? options.indexOf(selectedElement)
      : -1;

    let nextIndex;

    if (direction === 'up') {
      nextIndex = selectedIndex <= 0
        ? options.length - 1
        : selectedIndex - 1;
    } else {
      nextIndex = selectedIndex >= options.length - 1
        ? 0
        : selectedIndex + 1;
    }

    options.forEach((option) => {
      option.setAttribute('aria-selected', 'false');
    });

    const activeElement = options[nextIndex];

    activeElement.setAttribute('aria-selected', 'true');

    if (!activeElement.id) {
      activeElement.id = `predictive-search-option-${nextIndex}`;
    }

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );

    this.setLiveRegionText(activeElement.textContent.trim());
  }

  selectOption() {
    const selectedResult = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedResult) {
      selectedResult.click();
    }
  }

  getPredictiveSearchUrl() {
    if (
      window.routes &&
      typeof window.routes.predictive_search_url === 'string'
    ) {
      return window.routes.predictive_search_url;
    }

    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    return `${root.replace(/\/?$/, '/')}search/suggest`;
  }

  getCacheKey(searchTerm) {
    return searchTerm
      .toLowerCase()
      .replace(/\s+/g, '-');
  }

  cancelPendingRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  async getSearchResults(searchTerm) {
    const queryKey = this.getCacheKey(searchTerm);

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.cancelPendingRequest();
    this.abortController = new AbortController();

    const parameters = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search'
    });

    const requestUrl =
      `${this.getPredictiveSearchUrl()}?${parameters.toString()}`;

    try {
      const response = await fetch(requestUrl, {
        signal: this.abortController.signal,
        headers: {
          Accept: 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Predictive search request failed with status ${response.status}.`
        );
      }

      const responseText = await response.text();
      const documentFragment = new DOMParser().parseFromString(
        responseText,
        'text/html'
      );

      const renderedSection = documentFragment.querySelector(
        '#shopify-section-predictive-search'
      );

      if (!renderedSection) {
        throw new Error(
          'The predictive-search section was not found in the response. Confirm that sections/predictive-search.liquid exists.'
        );
      }

      const resultsMarkup = renderedSection.innerHTML.trim();

      this.cachedResults[queryKey] = resultsMarkup;
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(error);
      this.renderErrorState();
    } finally {
      this.abortController = null;
    }
  }

  setLiveRegionLoadingState() {
    this.loadingText =
      this.getAttribute('data-loading-text') || 'Loading';

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';

    window.setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    this.setLiveRegionResults();
    this.open();
  }

  renderErrorState() {
    this.removeAttribute('loading');
    this.removeAttribute('results');
    this.predictiveSearchResults.innerHTML = '';
    this.close();
  }

  setLiveRegionResults() {
    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    if (countElement) {
      this.setLiveRegionText(countElement.textContent.trim());
    }
  }

  getResultsMaxHeight() {
    const header =
      document.getElementById('shopify-section-header') ||
      document.querySelector('.site-header');

    const headerBottom = header
      ? header.getBoundingClientRect().bottom
      : 0;

    return Math.max(
      220,
      window.innerHeight - Math.max(headerBottom, 0) - 20
    );
  }

  open() {
    this.predictiveSearchResults.hidden = false;
    this.predictiveSearchResults.style.maxHeight =
      `${this.getResultsMaxHeight()}px`;

    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.cancelPendingRequest();

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.predictiveSearchResults.innerHTML = '';
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute('aria-selected', 'false');
    }

    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
